import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { fullName, getUserInitials } from '~/infra/utils';
import { BusinessPartyTypeEnum } from '~/store/store.model';
import { BusinessSettingsModel } from './BusinessSettingsModel';
export var ColumnPaymentsStatus = /*#__PURE__*/ function(ColumnPaymentsStatus) {
    ColumnPaymentsStatus["Enabled"] = "enabled";
    ColumnPaymentsStatus["Disabled"] = "disabled";
    return ColumnPaymentsStatus;
}({});
export var UserRole = /*#__PURE__*/ function(UserRole) {
    UserRole["Member"] = "member";
    UserRole["Admin"] = "admin";
    UserRole["Owner"] = "owner";
    UserRole["Accountant"] = "accountant";
    UserRole["AnchorAdmin"] = "anchorAdmin";
    UserRole["Reviewer"] = "reviewer";
    return UserRole;
}({});
export class WorkspaceModel {
    static fromDTO(dto) {
        return new WorkspaceModel(dto);
    }
    toDTO() {
        return this.data;
    }
    toIdAndName() {
        return {
            id: this.data.id,
            name: this.data.name
        };
    }
    id() {
        return this.data.id;
    }
    name() {
        return this.data.name;
    }
    setName(name) {
        return new WorkspaceModel(_object_spread_props(_object_spread({}, this.data), {
            name
        }));
    }
    logoUrl() {
        return this.data.logoUrl;
    }
    setupCompleted() {
        return this.data.setupCompleted;
    }
    isAccountant() {
        return this.data.role === "accountant";
    }
    isAnchorAdmin() {
        return this.data.role === "anchorAdmin";
    }
    isCustomer() {
        return this.data.type === BusinessPartyTypeEnum.Customer || this.data.type === BusinessPartyTypeEnum.MultiVendorCustomer;
    }
    isCustomerOfVendor(vendorId) {
        return this.data.type === BusinessPartyTypeEnum.Customer && this.data.customer.vendor.id === vendorId;
    }
    isVendor() {
        return this.data.type === BusinessPartyTypeEnum.Vendor;
    }
    isCompletelySetupAndDisplayable() {
        if (this.isAccountant() || this.isAnchorAdmin()) {
            return false;
        }
        return this.isCompletelySetup();
    }
    isDisplayableInSwitcher(vobEarlyCreation) {
        if (vobEarlyCreation && this.isVendor()) {
            return !this.isAccountant() && !this.isAnchorAdmin();
        }
        return this.isCompletelySetupAndDisplayable();
    }
    isCompletelySetup() {
        return this.isCustomer() && [
            "owner",
            "admin",
            "member"
        ].includes(this.data.role) || this.isVendor() && this.setupCompleted();
    }
    getBusinessSwitcherItemSubtext() {
        switch(this.data.type){
            case BusinessPartyTypeEnum.Vendor:
                return 'Service Provider account';
            case BusinessPartyTypeEnum.Customer:
                return "Client of ".concat(this.data.customer.vendor.name);
            case BusinessPartyTypeEnum.MultiVendorCustomer:
                return 'Client account';
        }
    }
    getBusinessSwitcherItemLogoUrl() {
        switch(this.data.type){
            case BusinessPartyTypeEnum.Vendor:
                return this.data.logoUrl || '';
            case BusinessPartyTypeEnum.Customer:
                return this.data.customer.vendor.logoUrl || '';
            case BusinessPartyTypeEnum.MultiVendorCustomer:
                return '';
        }
    }
    setLogoUrl(logoUrl) {
        return new WorkspaceModel(_object_spread_props(_object_spread({}, this.data), {
            logoUrl
        }));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
export class UserInfoModel {
    static fromDTO(dto) {
        return new UserInfoModel(dto);
    }
    id() {
        return this.data.id;
    }
    email() {
        return this.data.email;
    }
    firstName() {
        return this.data.firstName;
    }
    lastName() {
        return this.data.lastName;
    }
    fullName() {
        return fullName(this.data.firstName || null, this.data.lastName || null);
    }
    phone() {
        return this.data.phone;
    }
    userInitials() {
        return getUserInitials({
            firstName: this.data.firstName,
            lastName: this.data.lastName,
            email: this.data.email
        });
    }
    referredByBusinessId() {
        var _this_data_referredBy;
        return (_this_data_referredBy = this.data.referredBy) === null || _this_data_referredBy === void 0 ? void 0 : _this_data_referredBy.businessId;
    }
    hasApprovedTermsOfService() {
        return this.data.hasApprovedTermsOfService;
    }
    numClientsAMonth() {
        return this.data.numClientsAMonth;
    }
    oAuthProvider() {
        return this.data.oAuthProvider;
    }
    identityTypes() {
        return this.data.identityTypes;
    }
    hasIdentity() {
        var _this_data_identityTypes;
        return !!((_this_data_identityTypes = this.data.identityTypes) === null || _this_data_identityTypes === void 0 ? void 0 : _this_data_identityTypes.length);
    }
    loggedInViaLink() {
        return this.data.identityTypes === null;
    }
    hasPassword() {
        var _this_data_identityTypes;
        return !!((_this_data_identityTypes = this.data.identityTypes) === null || _this_data_identityTypes === void 0 ? void 0 : _this_data_identityTypes.includes('password'));
    }
    workspaces() {
        var _this_data_workspaces;
        return (_this_data_workspaces = this.data.workspaces) === null || _this_data_workspaces === void 0 ? void 0 : _this_data_workspaces.map(WorkspaceModel.fromDTO);
    }
    hasAVendorAccount() {
        return (this.workspaces() || []).some((workspace)=>workspace.isVendor());
    }
    business() {
        return this.data.worksAt;
    }
    hasBusiness() {
        return !!(this.data.worksAt && this.data.worksAt.name && (this.data.worksAt.businessTypes.length || this.data.worksAt.description));
    }
    businessId() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.id;
    }
    businessSettings() {
        return this._businessSettings;
    }
    businessName() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.name;
    }
    businessLogoUrl() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.logoUrl;
    }
    disabledPayments() {
        return this.data.worksAt ? this.data.worksAt.disablePayments : false;
    }
    /** Returns the role of the user in his current business. */ role() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.role;
    }
    isVendor() {
        var _this_data_worksAt;
        return ((_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.type) == BusinessPartyTypeEnum.Vendor;
    }
    isCustomer() {
        var _this_data_worksAt, _this_data_worksAt1;
        return ((_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.type) == BusinessPartyTypeEnum.Customer || ((_this_data_worksAt1 = this.data.worksAt) === null || _this_data_worksAt1 === void 0 ? void 0 : _this_data_worksAt1.type) == BusinessPartyTypeEnum.MultiVendorCustomer;
    }
    isOwner() {
        return this.role() === "owner";
    }
    isAdmin() {
        return this.role() === "admin";
    }
    isReviewer() {
        return this.role() === "reviewer";
    }
    paymentsDisabled() {
        var _this_data_worksAt;
        return !!((_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.disablePayments);
    }
    columnEntityCreated() {
        return !!this.data.worksAt && this.data.worksAt.column.paymentsStatus === "enabled";
    }
    hasColumnAccountId() {
        return !!this.data.worksAt && !!this.data.worksAt.column.accountId;
    }
    hasColumnWalletAccountId() {
        return !!this.data.worksAt && !!this.data.worksAt.column.walletAccountId;
    }
    hasColumnPayoutAccountId() {
        return !!this.data.worksAt && !!this.data.worksAt.column.payoutAccountId;
    }
    setWorksAt(worksAt) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            worksAt
        }));
    }
    setNumClientsAMonth(numClientsAMonth) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            numClientsAMonth
        }));
    }
    updateUserDetails(data) {
        return new UserInfoModel(_object_spread({}, this.data, data));
    }
    setBranding(branding) {
        const worksAt = this.data.worksAt ? _object_spread_props(_object_spread({}, this.data.worksAt), {
            branding
        }) : undefined;
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            worksAt
        }));
    }
    setWorkspaces(workspaces) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            workspaces
        }));
    }
    setWorkspaceModels(workspaces) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            workspaces: workspaces === null || workspaces === void 0 ? void 0 : workspaces.map((workspace)=>workspace.toDTO())
        }));
    }
    constructor(data){
        var _data_worksAt;
        _define_property(this, "data", void 0);
        _define_property(this, "_businessSettings", void 0);
        this.data = data;
        this._businessSettings = ((_data_worksAt = data.worksAt) === null || _data_worksAt === void 0 ? void 0 : _data_worksAt.settings) && BusinessSettingsModel.fromDTO(data.worksAt.settings);
    }
}
